/**
 * The model behind /tools/construction-cost-calculator.
 *
 * ── What this is for ────────────────────────────────────────────────────────
 * Every indexable page on this domain either describes the product or explains
 * a document. Neither earns links. A builder — or more often the person who is
 * about to hire one — searches "construction cost per sqft in Coimbatore"
 * long before they search for software, and nothing here answered that.
 *
 * A calculator also does something a guide cannot: it gets linked to. The
 * indexing problem this site has is not content quality, it is that nothing
 * anywhere links here, and a free tool is the one page type that strangers
 * link to unprompted.
 *
 * ── The honesty constraint, which shapes the whole design ───────────────────
 * This site does not publish numbers it cannot stand behind — see the
 * BUSINESS_CONTACT comment in marketingMetadata.ts for the same rule applied
 * to the business address. We have no rate dataset. We are not a quantity
 * surveyor and we have not surveyed Tamil Nadu construction rates.
 *
 * So the defaults below are explicitly INDICATIVE STARTING POINTS, presented
 * as a band rather than a figure, and every one of them is editable in the UI.
 * The page says so in the open, next to the number, not in small print at the
 * bottom. A calculator that hides its assumptions is just an opinion with a
 * rupee sign on it.
 *
 * That is also the honest bridge to the product: the whole argument of the
 * Estimates module is that a quotation should be built from YOUR rates, not a
 * generic per-sqft figure. A tool that insists the reader overwrite our
 * defaults is making the product's own case.
 */

/** A build-quality tier, as builders and clients actually discuss it. */
export interface CostTier {
  key: 'basic' | 'standard' | 'premium';
  label: string;
  /** One line on what the money is actually buying. */
  blurb: string;
  /** Indicative ₹/sq.ft band for Tamil Nadu. Editable in the UI. */
  low: number;
  high: number;
  /** What a reader would recognise at this level. */
  marks: string[];
}

export const COST_TIERS: CostTier[] = [
  {
    key: 'basic',
    label: 'Basic',
    blurb: 'A sound, plain house. Nothing decorative, nothing imported.',
    low: 1650,
    high: 1900,
    marks: ['Vitrified tile flooring', 'Local brand CP and sanitary ware', 'Flush doors, standard windows', 'Single-coat emulsion'],
  },
  {
    key: 'standard',
    label: 'Standard',
    blurb: 'What most self-build homes in Tamil Nadu are actually finished to.',
    low: 1900,
    high: 2300,
    marks: ['Branded vitrified tiles', 'Mid-range CP and sanitary fittings', 'Teak or engineered main door', 'Modular switches, false ceiling in hall'],
  },
  {
    key: 'premium',
    label: 'Premium',
    blurb: 'Architect-specified finishes, where the spec drives the cost more than the area does.',
    low: 2300,
    high: 3000,
    marks: ['Imported or large-format tiles, stone', 'Premium CP and sanitary ware', 'Solid teak joinery', 'Designed lighting, full false ceiling'],
  },
];

export const tierByKey = (k: string) => COST_TIERS.find((t) => t.key === k) ?? COST_TIERS[1];

/**
 * Where the money goes, as a share of the build cost.
 *
 * These shares are the conventional split for a residential RCC-framed build
 * in Tamil Nadu and are stable enough to be useful — the structure is always
 * the biggest line, finishes always move the most between tiers. They are
 * shares, not rates: they tell a reader which head to interrogate first, which
 * is the actual job of a breakdown.
 *
 * Must sum to 100. There is a test for that below, because a silent drift here
 * would quietly misstate every head on the page.
 */
export interface CostHead {
  key: string;
  label: string;
  share: number;
  /** What this head covers, in the words a site engineer would use. */
  covers: string;
}

export const COST_HEADS: CostHead[] = [
  { key: 'foundation', label: 'Foundation & earthwork', share: 12, covers: 'Excavation, PCC, footings, plinth beam and backfilling.' },
  { key: 'structure', label: 'RCC structure', share: 28, covers: 'Cement, steel, aggregate, shuttering and labour for columns, beams and slabs. The single largest head on almost every job.' },
  { key: 'masonry', label: 'Brickwork & plastering', share: 14, covers: 'Block or brick walls, internal and external plaster, and the scaffolding for it.' },
  { key: 'flooring', label: 'Flooring & tiling', share: 11, covers: 'Floor tiles, skirting, bathroom and kitchen dado, laying labour.' },
  { key: 'joinery', label: 'Doors & windows', share: 9, covers: 'Main door, internal doors, window frames and shutters, grills and hardware.' },
  { key: 'electrical', label: 'Electrical', share: 7, covers: 'Conduiting, wiring, switches, DB, fixtures and the earthing.' },
  { key: 'plumbing', label: 'Plumbing & sanitary', share: 7, covers: 'Water supply and drainage lines, CP fittings, sanitary ware, overhead and sump tanks.' },
  { key: 'painting', label: 'Painting & finishes', share: 7, covers: 'Putty, primer, interior and exterior paint, and the finishing trades.' },
  { key: 'site', label: 'Site expenses & contingency', share: 5, covers: 'Water, power, supervision, temporary works, wastage and the things nobody budgets for.' },
];

/** Shares must total 100 — a drift here misstates every row silently. */
export const COST_HEAD_TOTAL = COST_HEADS.reduce((a, h) => a + h.share, 0);

export interface CostInput {
  /** Built-up area, sq.ft. Not plot area — see the FAQ. */
  areaSqft: number;
  /** ₹ per sq.ft. Seeded from the tier, then owned by the reader. */
  ratePerSqft: number;
}

export interface CostResult {
  total: number;
  heads: { key: string; label: string; covers: string; share: number; amount: number }[];
}

export function computeCost({ areaSqft, ratePerSqft }: CostInput): CostResult {
  const area = Number.isFinite(areaSqft) && areaSqft > 0 ? areaSqft : 0;
  const rate = Number.isFinite(ratePerSqft) && ratePerSqft > 0 ? ratePerSqft : 0;
  const total = Math.round(area * rate);
  return {
    total,
    heads: COST_HEADS.map((h) => ({
      key: h.key,
      label: h.label,
      covers: h.covers,
      share: h.share,
      // Rounded per head rather than apportioned from a rounded total: the
      // rows are read individually, and a reader who adds them up is allowed
      // to be a few rupees out on a number that is a band anyway.
      amount: Math.round((total * h.share) / 100),
    })),
  };
}

/**
 * Indian digit grouping — 12,34,567 rather than 1,234,567.
 *
 * Intl with en-IN does this correctly and is in every browser and in Node, so
 * there is no reason to hand-roll the lakh/crore grouping.
 */
export const inr = (n: number): string =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(n));

/** ₹1.23 Cr / ₹45.6 L / ₹9,000 — for the headline figure only. */
export function inrShort(n: number): string {
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2)} Cr`;
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(1)} L`;
  return `₹${inr(n)}`;
}

/** Land units a Tamil Nadu reader will have the plot measured in. */
export const LAND_UNITS: { key: string; label: string; sqft: number; note: string }[] = [
  { key: 'sqft', label: 'Square feet', sqft: 1, note: '' },
  { key: 'cent', label: 'Cent', sqft: 435.6, note: '1 cent = 435.6 sq.ft' },
  { key: 'ground', label: 'Ground', sqft: 2400, note: '1 ground = 2,400 sq.ft' },
  { key: 'acre', label: 'Acre', sqft: 43560, note: '1 acre = 100 cents' },
];

export const DEFAULT_AREA_SQFT = 1200;

export const COST_FAQS: { q: string; a: string }[] = [
  {
    q: 'Is this built-up area or plot area?',
    a: 'Built-up area — the total constructed area across all floors, measured to the outside of the walls. A 2,400 sq.ft plot with a 1,200 sq.ft ground floor and a 1,000 sq.ft first floor is 2,200 sq.ft of built-up area, not 2,400. Getting this wrong is the commonest reason a per-sqft estimate comes out badly.',
  },
  {
    q: 'Where do the default rates come from, and should I trust them?',
    a: 'They are indicative bands for Tamil Nadu, not quotes, and they are meant to be overwritten. Rates move with steel and cement prices, with the district, and with how far the site is from a ready-mix plant. The figure that matters is the one you get from your own last two or three jobs — the rate box is editable for exactly that reason.',
  },
  {
    q: 'What is not included in a per-square-foot rate?',
    a: 'Normally: the land, compound wall and gate, borewell or water connection, EB service connection and deposits, approval and liaison fees, interiors and loose furniture, landscaping, and GST. These are quoted separately on most jobs, and a client who assumes they are inside the per-sqft number is the client who is unhappy at handover.',
  },
  {
    q: 'Why is the RCC structure the biggest head?',
    a: 'Because cement, steel and the labour to place them are consumed across every floor plate, and unlike finishes they cannot be downgraded without changing the building. It is also the head most exposed to price movement, which is why a quote held open for six months is a risk to the builder and not to the client.',
  },
  {
    q: 'Can I use this as a quotation for my client?',
    a: 'No, and you should not. A per-square-foot figure is a conversation opener — it tells a client whether their budget and their drawing are in the same universe. A quotation needs your own rates, the actual specification, a payment schedule and GST. That is what the Estimates module in BuilderSiteNow produces, and it starts from your rate book rather than from a generic average.',
  },
  {
    q: 'Does the cost per square foot fall on a larger house?',
    a: 'A little, and less than people expect. Foundation, plumbing cores and the site establishment are spread over more area, so the rate eases slightly. But finishes scale almost linearly, and on larger homes the specification usually rises at the same time — which is why a bigger house often costs more per square foot, not less.',
  },
];
